/**
 * lpChatBoard.ts — LP 管理画面（/admin）を LINE ページに統合するための純関数（2026-10-26）
 *
 * 設計: booking-platform/md/customer-chat.md §13 ／ md/master_md/page-line-chat.md「/admin の統合」
 *
 * - 匿名チャット（LP の session_key が `res_` 以外）を LINE ページのインスタンスに変換する（kind='anon'）
 * - 種類タブ「すべて / LINE / 予約チャット / 匿名チャット」と匿名の区分（要対応 / 自動応答のみ / ジャンク）の絞り込み
 * - 同じ予約の LINE と予約チャットを「すべて」タブで 1 枚にまとめる（LINE のカードに sibling_resv を付ける）
 * - 予約に紐づいていない LINE ユーザーの候補（予約チャットのパネルで 1 クリック紐づけ）
 * React・fetch に依存しないので、ここに置いた判定はすべて単体テストで固定する。
 */
import { isReservationSessionKey, isSessionUnreadForStaff, isLpSessionArchived, compareBoardOrder, RESV_INSTANCE_PREFIX, type ReservationChatSession } from './reservationChat';

export type ChatKind = 'line' | 'resv' | 'anon';
/** archive = アーカイブした会話だけ（LINE・自社チャットのアーカイブと対応完了。2026-10-26 §13-5） */
export type KindTab = 'all' | 'line' | 'resv' | 'anon' | 'archive';
/** LP の chat_sessions.triage。未判定は自動応答のみ扱い（/admin と同じ） */
export type AnonTriage = 'attention' | 'auto' | 'junk' | 'all';

export const KIND_TABS: ReadonlyArray<{ value: KindTab; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'line', label: 'LINE' },
  { value: 'resv', label: '予約チャット' },
  { value: 'anon', label: '匿名チャット' },
  { value: 'archive', label: 'アーカイブ' },
];
export const ANON_TRIAGE_TABS: ReadonlyArray<{ value: AnonTriage; label: string }> = [
  { value: 'attention', label: '要対応' },
  { value: 'auto', label: '自動応答のみ' },
  { value: 'junk', label: 'ジャンク' },
  { value: 'all', label: 'すべて' },
];

/** 匿名チャットを一覧に出す範囲（LP の get_sessions scope=board と同じ。旧 PHP のときはここで絞る） */
export const ANON_BOARD_DAYS = 30;

export const chatKindOf = (inst: any): ChatKind => (inst?.kind === 'resv' ? 'resv' : inst?.kind === 'anon' ? 'anon' : 'line');
/** LP の chat.php で扱う会話か（予約チャット・匿名チャット） */
export const isLpChatInstance = (inst: any): boolean => inst?.kind === 'resv' || inst?.kind === 'anon';

export const anonTriageOf = (s: { triage?: string | null; [key: string]: any } | null | undefined): Exclude<AnonTriage, 'all'> => {
  const t = s?.triage;
  return t === 'attention' || t === 'junk' ? t : 'auto';
};

const toTime = (v: unknown): number => {
  if (typeof v !== 'string' || !v) return 0;
  const t = new Date(v.includes('T') ? v : v.replace(/-/g, '/')).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * 会話の最終活動時刻。LP の `last_message_at`（最後のメッセージの created_at・2026-10-26 追加）→ `last_user_at` → `updated_at`。
 * `updated_at` は TIMESTAMP の ON UPDATE で既読・地域の更新や一括 UPDATE のたびに書き換わるので最後の手段
 * （2026-10-11 10:55 の一括更新で全件が同じ時刻になっていた）。
 */
export const sessionActivityAt = (s: ReservationChatSession): string | null =>
  (typeof s.last_message_at === 'string' && s.last_message_at) || s.last_user_at || s.updated_at || null;

/**
 * get_sessions の結果から匿名チャットを選ぶ。
 * アーカイブ済みは showArchived のときだけ。ピン留め以外は直近 days 日に動いたものだけ（旧 PHP が全件を返しても同じ範囲にする）。
 */
export const selectAnonSessions = (
  sessions: ReservationChatSession[] | null | undefined,
  showArchived: boolean,
  nowMs: number,
  days: number = ANON_BOARD_DAYS,
): ReservationChatSession[] => {
  const since = nowMs - days * 86400000;
  return (Array.isArray(sessions) ? sessions : []).filter((s) => {
    if (!s || typeof s.session_key !== 'string' || !s.session_key || isReservationSessionKey(s.session_key)) return false;
    if (!showArchived && isLpSessionArchived(s)) return false;
    if (Number(s.is_pinned || 0)) return true;
    return toTime(sessionActivityAt(s)) >= since;
  });
};

/** 匿名チャットの表示名: 予約前の会話なら「{氏名} 様（予約前）」、それ以外は「匿名 · {都市/地域/国}」 */
export const anonDisplayName = (s: ReservationChatSession): string => {
  const name = typeof s.resv_customer_name === 'string' ? s.resv_customer_name.trim() : '';
  if (name) return `${name} 様（予約前）`;
  const place = [s.city, s.region, s.country].map((v) => (typeof v === 'string' ? v.trim() : '')).find((v) => v);
  if (place) return `匿名 · ${place}`;
  return `匿名 ${String(s.session_key).slice(-6)}`;
};

/** 匿名チャットを LINE ページのカード／フィルタが読む形に変換する（toReservationChatInstance と同じキー名） */
export function toAnonChatInstance(s: ReservationChatSession): Record<string, any> {
  const name = anonDisplayName(s);
  const unread = isSessionUnreadForStaff(s);
  const preResvId = s.reservation_id != null && String(s.reservation_id) !== '' ? String(s.reservation_id) : null;
  return {
    kind: 'anon',
    session_key: s.session_key,
    instanceKey: `${RESV_INSTANCE_PREFIX}${s.session_key}`,
    line_user_id: `${RESV_INSTANCE_PREFIX}${s.session_key}`,
    line_display_name: name,
    custom_display_name: name,
    line_picture_url: null,
    contact_code: typeof s.contact_code === 'string' && s.contact_code ? s.contact_code : null,
    unread_count: unread ? 1 : 0,
    last_message_at: sessionActivityAt(s),
    last_message_content: s.last_message || '',
    last_sender: s.last_sender || null,
    is_pinned: Boolean(Number(s.is_pinned || 0)),
    is_archived: isLpSessionArchived(s),
    staff_archived: Boolean(s.staff_archived_at),
    chat_closed: s.status === 'ARCHIVED',
    alert_level: Number(s.alert_level || 0),
    admin_handling: Boolean(s.admin_handling),
    memo: s.memo || '',
    tags: [],
    // 匿名は予約の行に展開しない（予約前の会話は pre_reservation_id で予約チャットのパネルから辿る）
    reservation_ids: [],
    linked_reservations: [],
    instance_reservation_id: null,
    instance_res_data: null,
    pre_reservation_id: preResvId,
    hasOtherReservations: false,
    reservation_statuses: [],
    triage: anonTriageOf(s),
    client_ip: s.client_ip || null,
    country: s.country || null,
    country_code: s.country_code || null,
    region: s.region || null,
    city: s.city || null,
    is_blocked: Boolean(Number(s.is_blocked || 0)),
    block_reason_type: s.block_reason_type || null,
  };
}

/** 一覧から外れているか（LINE の is_archived は 0/1 で来ることがある） */
export const isInstanceArchived = (i: Record<string, any>): boolean => Boolean(Number(i?.is_archived || 0)) || i?.is_archived === true;

/**
 * 種類タブと匿名の区分で絞る。
 * 「すべて」: LINE・予約チャット全部＋匿名は要対応だけ（自動応答のみ・ジャンクで一覧を埋めない）
 * 「アーカイブ」: アーカイブした会話だけ（種類を問わない）。ほかのタブにはアーカイブを出さない
 */
export function filterByKindTab<T extends Record<string, any>>(instances: T[], tab: KindTab, anonTriage: AnonTriage): T[] {
  return instances.filter((i) => {
    if (tab === 'archive') return isInstanceArchived(i);
    if (isInstanceArchived(i)) return false;
    const k = chatKindOf(i);
    if (tab === 'line') return k === 'line';
    if (tab === 'resv') return k === 'resv';
    if (tab === 'anon') return k === 'anon' && (anonTriage === 'all' || i.triage === anonTriage);
    return k !== 'anon' || i.triage === 'attention';
  });
}

/** タブ・区分の件数（ラベル用）。匿名の「要対応で未返信」は赤バッジ */
export function countByKind(instances: Record<string, any>[]) {
  const c = { line: 0, resv: 0, anon: 0, attention: 0, auto: 0, junk: 0, attentionUnreplied: 0, all: 0, archive: 0 };
  instances.forEach((i) => {
    if (isInstanceArchived(i)) {
      c.archive += 1;
      return;
    }
    const k = chatKindOf(i);
    c[k] += 1;
    if (k === 'anon') {
      const t = (i.triage || 'auto') as 'attention' | 'auto' | 'junk';
      c[t] += 1;
      if (t === 'attention' && i.last_sender === 'user') c.attentionUnreplied += 1;
    }
  });
  c.all = c.line + c.resv + c.attention;
  return c;
}

const effectiveUnread = (i: Record<string, any>) => (i.unread_count || 0) > 0 || Boolean(i.sibling_resv_unread);
const effectiveTime = (i: Record<string, any>) => Math.max(toTime(i.last_message_at), toTime(i.sibling_resv?.last_message_at));

/**
 * 同じ予約の LINE と予約チャットを 1 枚にまとめる（「すべて」タブ用）。
 * 予約チャットのカードは消し、その予約の LINE のカードに `sibling_resv`（予約チャットのインスタンス）と
 * `sibling_resv_unread`（予約チャットに新着あり）を付ける。並びは未読（どちらか）→ ピン留め → 新しい方の時刻（compareBoardOrder）。
 * まとめる相手が無い予約チャットはそのまま残す。
 */
export function bundleReservationSiblings<T extends Record<string, any>>(instances: T[]): T[] {
  const lineByResv = new Map<string, number>();
  instances.forEach((i, idx) => {
    if (chatKindOf(i) !== 'line' || i.instance_reservation_id == null) return;
    const rid = String(i.instance_reservation_id);
    if (!lineByResv.has(rid)) lineByResv.set(rid, idx);
  });
  if (lineByResv.size === 0) return instances;
  const siblingOf = new Map<number, T>();
  const absorbed = new Set<number>();
  instances.forEach((i, idx) => {
    if (chatKindOf(i) !== 'resv' || i.instance_reservation_id == null) return;
    const lineIdx = lineByResv.get(String(i.instance_reservation_id));
    if (lineIdx === undefined || siblingOf.has(lineIdx)) return;
    siblingOf.set(lineIdx, i);
    absorbed.add(idx);
  });
  if (absorbed.size === 0) return instances;
  const out: T[] = [];
  instances.forEach((i, idx) => {
    if (absorbed.has(idx)) return;
    const sib = siblingOf.get(idx);
    out.push(sib ? ({ ...i, sibling_resv: sib, sibling_resv_unread: (sib.unread_count || 0) > 0 } as T) : i);
  });
  return out.sort((a, b) => compareBoardOrder(a, b, effectiveUnread, effectiveTime));
}

const nameTokens = (v: unknown): string[] =>
  typeof v === 'string'
    ? v.toLowerCase().replace(/(様|さま)$/u, '').split(/[\s　・,.、]+/u).map((t) => t.trim()).filter((t) => t.length >= 2)
    : [];
const compact = (v: unknown): string => (typeof v === 'string' ? v.toLowerCase().replace(/[\s　・]+/gu, '').replace(/(様|さま)$/u, '') : '');

export const LINE_LINK_CANDIDATE_DAYS = 14;

/**
 * 予約チャットのお客様と同じ人らしい LINE ユーザーの候補（最大 max 件・新しい順）。自動では紐づけない（同名の別人がいる）。
 * 条件: 表示名（LINE 名・スタッフが付けた名前）の単語が予約の氏名の単語と一致（漢字など空白の無い氏名は全体一致）・
 * 直近 days 日にやり取りがある・まだこの予約に紐づいていない・スタッフチャットではない。
 */
export function findLineLinkCandidates<U extends Record<string, any>>(
  customerName: string | null | undefined,
  lineUsers: U[] | null | undefined,
  reservationId: string | number | null | undefined,
  nowMs: number,
  max = 3,
  days = LINE_LINK_CANDIDATE_DAYS,
): U[] {
  const want = nameTokens(customerName);
  const wantCompact = compact(customerName);
  if (want.length === 0 && wantCompact.length < 2) return [];
  const rid = reservationId != null ? String(reservationId) : '';
  const since = nowMs - days * 86400000;
  const seen = new Set<string>();
  return (Array.isArray(lineUsers) ? lineUsers : [])
    .filter((u) => {
      if (!u || typeof u.line_user_id !== 'string' || u.line_user_id.startsWith(RESV_INSTANCE_PREFIX)) return false;
      if (seen.has(u.line_user_id)) return false;
      const ids: string[] = (u.reservation_ids || []).map((x: unknown) => String(x));
      if (ids.some((x) => x.startsWith('STAFF:'))) return false;
      if (rid && ids.includes(rid)) return false;
      if (toTime(u.last_message_at) < since) return false;
      const names = [u.custom_display_name, u.line_display_name];
      const tokens = names.flatMap(nameTokens);
      const hit = tokens.some((t) => want.includes(t))
        || (wantCompact.length >= 2 && names.some((n) => compact(n) === wantCompact));
      if (hit) seen.add(u.line_user_id);
      return hit;
    })
    .sort((a, b) => toTime(b.last_message_at) - toTime(a.last_message_at))
    .slice(0, max);
}

/** 予約の「予約前の会話」（匿名の会話のうち、本人確認でこの予約に結びついたもの）。新しい順 */
export const preReservationSessions = (
  sessions: ReservationChatSession[] | null | undefined,
  reservationId: string | number | null | undefined,
): ReservationChatSession[] => {
  if (reservationId == null || String(reservationId) === '') return [];
  const rid = String(reservationId);
  return (Array.isArray(sessions) ? sessions : [])
    .filter((s) => s && !isReservationSessionKey(s.session_key) && s.reservation_id != null && String(s.reservation_id) === rid)
    .sort((a, b) => toTime(b.updated_at) - toTime(a.updated_at));
};
